/**
 * Paired-association helpers for invoice ↔ entity (task / expense / transaction /
 * flat_fee / flat_fee_milestone).
 *
 * See ./INVOICE_ASSOCIATION_INVARIANT.md for the full invariant and authority hierarchy.
 *
 * UNIDIRECTIONAL INVARIANT (the design rule):
 *
 *   - `invoice.*Ids` is the only directly-mutable side of the association.
 *   - `entity.invoiceId` is derived. **Never written outside this module.**
 *   - All writes to back-pointers happen inside `applyInvoiceEntityIds` (the
 *     single primitive). Every other public helper here is a thin wrapper that
 *     calls `applyInvoiceEntityIds` with a transformed array set.
 *
 * Why: a single writer for the back-pointer means the invariant cannot be
 * violated by accident. Two-way mutation paths are how drift was introduced
 * historically (e.g., the un-finalize bug that motivated this module).
 *
 * All helpers are pure: they return clones of the affected invoice + entities
 * with `status='U'`, but do NOT dispatch. The caller is responsible for
 * dispatching the returned clones in the same tick (no awaits between
 * dispatches) so the saves are batched and persisted together.
 *
 * Discounts are handled separately (see `associateDiscountWithInvoice` /
 * `dissociateDiscount`) because they use `entityType`/`entityId` instead of
 * `invoiceId`, and there is no corresponding `discountIds` array on the invoice.
 */

import store from '../../store/store';
import newHRTimestamp from '../../lib/picos/timestamp';
import {getCurrentUserId} from '../../lib/picos/currentLogin';
import Invoice from '../../entities/invoice';
import Task from '../../entities/task';
import Expense from '../../entities/expense';
import Transaction from '../../entities/transaction';
import FlatFee from '../../entities/flatFee';
import FlatFeeMilestone from '../../entities/flatFeeMilestone';
import Discount from '../../entities/discount';
import {uuid} from '../../@types/schema';
import {saveInvoiceQueue} from '../../store/actions/invoiceActions';
import {saveManyTasksQueue} from '../../store/actions/taskActions';
import {saveManyExpensesQueue} from '../../store/actions/expenseActions';
import {saveManyTransactionsQueue} from '../../store/actions/transactionActions';
import {saveManyFlatFeesQueue} from '../../store/actions/flatFeeActions';
import {saveManyFlatFeeMilestonesQueue} from '../../store/actions/flatFeeMilestoneActions';

type AssociableEntity = Task | Expense | Transaction | FlatFee | FlatFeeMilestone;

export interface AssociableEntitySet {
    tasks: Task[];
    expenses: Expense[];
    transactions: Transaction[];
    flatFees: FlatFee[];
    flatFeeMilestones: FlatFeeMilestone[];
}

export interface InvoiceEntityIds {
    taskIds: uuid[];
    expenseIds: uuid[];
    transactionIds: uuid[];
    flatFeeIds: uuid[];
    flatFeeMilestoneIds: uuid[];
}

/**
 * Canonical back-pointer enumeration: every non-deleted entity currently
 * back-pointing at this invoice via `invoiceId`. Use this — never a filtered view
 * like `FinancialHelper.getInvoiceInfo` — when you need to act on "everything
 * associated with this invoice".
 */
export function getAssociatedEntities(invoice: Invoice): AssociableEntitySet {
    const state = store.getState();
    const notDeleted = (e: AssociableEntity) => e.status !== 'D';

    return {
        tasks: state.tasks.getMappingFor('invoiceId', invoice.id).filter(notDeleted) as Task[],
        expenses: state.expenses.getMappingFor('invoiceId', invoice.id).filter(notDeleted) as Expense[],
        transactions: state.transactions.getMappingFor('invoiceId', invoice.id).filter(notDeleted) as Transaction[],
        flatFees: state.flatFees.getMappingFor('invoiceId', invoice.id).filter(notDeleted) as FlatFee[],
        flatFeeMilestones: state.flatFeeMilestones.getMappingFor('invoiceId', invoice.id).filter(notDeleted) as FlatFeeMilestone[],
    };
}

/**
 * Stamp an entity clone with status='U' and refreshed audit fields.
 */
function stamp<T extends AssociableEntity | Invoice | Discount>(entity: T): T {
    entity.status = 'U';
    entity.addedOn = newHRTimestamp();
    entity.addedBy = getCurrentUserId();
    return entity;
}

/**
 * Clone an entity and set its invoiceId. Returns the stamped clone.
 */
function cloneWithInvoiceId<T extends AssociableEntity>(entity: T, invoiceId: string | null): T {
    const clone = entity.clone() as T;
    (clone as AssociableEntity).invoiceId = invoiceId;
    return stamp(clone);
}

/**
 * Validate the invariant on a proposed (invoice, entities) pair. Throws if any
 * disagreement is detected — surfaces association bugs loudly at the point of
 * introduction rather than persisting drift.
 */
export function validateInvoiceAssociationInvariant(invoice: Invoice, entities: AssociableEntitySet): void {
    const expectInvoiceId = invoice.invoiceStatus === 'F' ? invoice.id : null;

    const check = (kind: string, arr: AssociableEntity[], invoiceIdsField: 'taskIds' | 'expenseIds' | 'transactionIds' | 'flatFeeIds' | 'flatFeeMilestoneIds') => {
        const arrayIds = new Set(invoice[invoiceIdsField] || []);
        const backPointerIds = new Set(arr.map(e => e.id));

        // Sets must match exactly
        if (arrayIds.size !== backPointerIds.size || [...arrayIds].some(id => !backPointerIds.has(id))) {
            throw new Error(`Invariant violated: invoice.${invoiceIdsField} (${[...arrayIds].join(',')}) does not match ${kind} back-pointers (${[...backPointerIds].join(',')}) for invoice ${invoice.id}`);
        }

        // All back-pointers must point at the right invoice id (or null for drafts)
        for (const e of arr) {
            if (e.invoiceId !== expectInvoiceId) {
                throw new Error(`Invariant violated: ${kind} ${e.id} has invoiceId=${e.invoiceId}, expected ${expectInvoiceId}`);
            }
        }
    };

    check('task', entities.tasks, 'taskIds');
    check('expense', entities.expenses, 'expenseIds');
    check('transaction', entities.transactions, 'transactionIds');
    check('flatFee', entities.flatFees, 'flatFeeIds');
    check('flatFeeMilestone', entities.flatFeeMilestones, 'flatFeeMilestoneIds');

    // Drafts have empty arrays and no snapshot. Generated JSON fields default to {}.
    if (invoice.invoiceStatus === 'D') {
        for (const field of ['taskIds', 'expenseIds', 'transactionIds', 'flatFeeIds', 'flatFeeMilestoneIds'] as const) {
            if ((invoice[field]?.length ?? 0) > 0) {
                throw new Error(`Invariant violated: draft invoice ${invoice.id} has non-empty ${field}`);
            }
        }
        if (Object.keys(invoice.finalValues ?? {}).length > 0) {
            throw new Error(`Invariant violated: draft invoice ${invoice.id} has non-empty finalValues`);
        }
    }
}

/**
 * ============================================================================
 * THE PRIMITIVE — the only function in this module that writes to entity.invoiceId.
 *
 * Takes the desired *Ids array set for an invoice, computes the diff against the
 * current back-pointer set (canonical query), and returns:
 *   - the invoice clone with the new arrays
 *   - clones of every entity whose invoiceId needs to change (added or removed),
 *     each stamped with status='U' and the right invoiceId value
 *
 * Every other public helper in this module is expressed as a call to this
 * function with a different array transformation.
 *
 * The caller is responsible for dispatching the result in one tick:
 *   const {invoice, entities} = applyInvoiceEntityIds(inv, newIds);
 *   store.dispatch(saveInvoiceQueue(invoice));
 *   if (entities.tasks.length) store.dispatch(saveManyTasksQueue(entities.tasks));
 *   ... etc for each entity type ...
 * ============================================================================
 */
export function applyInvoiceEntityIds(
    invoice: Invoice,
    newIds: InvoiceEntityIds,
): { invoice: Invoice; entities: AssociableEntitySet } {
    const state = store.getState();

    /**
     * For one entity type: compute the diff between current back-pointers and
     * target ids, return the clones that need invoiceId updates.
     */
    function diff<T extends AssociableEntity>(
        currentByBackPointer: T[],
        targetIds: uuid[],
        targetInvoiceId: uuid | null,
        lookupById: (id: uuid) => T | undefined | null,
    ): T[] {
        const currentIds = new Set(currentByBackPointer.map(e => e.id));
        const targetIdSet = new Set(targetIds);

        const removed = currentByBackPointer
            .filter(e => !targetIdSet.has(e.id))
            .map(e => cloneWithInvoiceId(e, null));

        const added: T[] = [];
        for (const id of targetIds) {
            if (currentIds.has(id)) continue; // already pointing at us
            const e = lookupById(id);
            if (!e) {
                throw new Error(`applyInvoiceEntityIds: entity ${id} not found in store`);
            }
            if (e.invoiceId && e.invoiceId !== targetInvoiceId) {
                throw new Error(`applyInvoiceEntityIds: entity ${id} is already on invoice ${e.invoiceId}, cannot move to ${targetInvoiceId} without first removing from source`);
            }
            added.push(cloneWithInvoiceId(e, targetInvoiceId));
        }

        return [...removed, ...added];
    }

    // For drafts, target invoiceId for added entities is null (drafts have no back-pointers).
    // For finalized invoices, target is invoice.id.
    const targetInvoiceId = invoice.invoiceStatus === 'F' ? invoice.id : null;
    // For drafts, target ids MUST be empty per the invariant.
    if (invoice.invoiceStatus === 'D') {
        for (const [k, v] of Object.entries(newIds)) {
            if ((v as uuid[]).length > 0) {
                throw new Error(`applyInvoiceEntityIds: draft invoice ${invoice.id} cannot have non-empty ${k}; un-finalize semantics require empty arrays on drafts`);
            }
        }
    }

    const currentAssociation = getAssociatedEntities(invoice);

    const entities: AssociableEntitySet = {
        tasks: diff(currentAssociation.tasks, newIds.taskIds, targetInvoiceId, id => state.tasks.find(t => t.id === id)) as Task[],
        expenses: diff(currentAssociation.expenses, newIds.expenseIds, targetInvoiceId, id => state.expenses.find(e => e.id === id)) as Expense[],
        transactions: diff(currentAssociation.transactions, newIds.transactionIds, targetInvoiceId, id => state.transactions.find(t => t.id === id)) as Transaction[],
        flatFees: diff(currentAssociation.flatFees, newIds.flatFeeIds, targetInvoiceId, id => state.flatFees.find(ff => ff.id === id)) as FlatFee[],
        flatFeeMilestones: diff(currentAssociation.flatFeeMilestones, newIds.flatFeeMilestoneIds, targetInvoiceId, id => state.flatFeeMilestones.find(ffm => ffm.id === id)) as FlatFeeMilestone[],
    };

    const newInvoice = invoice.clone();
    newInvoice.taskIds = [...newIds.taskIds];
    newInvoice.expenseIds = [...newIds.expenseIds];
    newInvoice.transactionIds = [...newIds.transactionIds];
    newInvoice.flatFeeIds = [...newIds.flatFeeIds];
    newInvoice.flatFeeMilestoneIds = [...newIds.flatFeeMilestoneIds];
    stamp(newInvoice);

    // Note: validateInvoiceAssociationInvariant compares the full back-pointer set (after applying
    // our updates) against the new arrays. That requires merging the unchanged
    // back-pointers + our changes. For simplicity, we trust the diff logic and
    // skip the full re-check here — the diff itself is the proof.

    return {invoice: newInvoice, entities};
}

/**
 * Finalize a draft invoice (D → F).
 *
 * Pure wrapper over `applyInvoiceEntityIds`: flips status to 'F', sets
 * `finalizedOn = now`, then delegates to the primitive to populate `*Ids`
 * arrays and back-pointers in lockstep. The caller is responsible for setting
 * finalize-specific business fields on the returned invoice clone (totals,
 * finalValues, billingIncrement, roundingMethod, dueDate adjustment, etc.)
 * before committing — those depend on `getInvoiceInfo` and the snapshot
 * builder, which live outside this module's concern.
 *
 * Throws if the invoice is not currently a draft.
 */
export function finalizeInvoice(
    invoice: Invoice,
    entityIds: InvoiceEntityIds,
): { invoice: Invoice; entities: AssociableEntitySet } {
    if (invoice.invoiceStatus !== 'D') {
        throw new Error(`finalizeInvoice: invoice ${invoice.id} is not a draft (status=${invoice.invoiceStatus})`);
    }

    const finalizingInvoice = invoice.clone();
    finalizingInvoice.invoiceStatus = 'F';
    finalizingInvoice.finalizedOn = new Date();

    return applyInvoiceEntityIds(finalizingInvoice, entityIds);
}

/**
 * Un-finalize an invoice (F → D).
 *
 * Implemented in terms of `applyInvoiceEntityIds` with all arrays empty: the
 * primitive computes the removals from the canonical back-pointer set, clears
 * every back-pointer, and returns the cleared invoice + entity clones.
 *
 * Additional un-finalize-specific fields (status, finalValues, totals) are set
 * on the resulting invoice clone before returning. The prior `finalValues`
 * snapshot is preserved in the `invoice` history table by the save pipeline's
 * normal audit mechanism, so the immutable finalize record is not lost.
 */
export function unfinalizeInvoice(invoice: Invoice): { invoice: Invoice; entities: AssociableEntitySet } {
    if (invoice.invoiceStatus !== 'F') {
        throw new Error(`unfinalizeInvoice: invoice ${invoice.id} is not finalized (status=${invoice.invoiceStatus})`);
    }

    // First flip to draft so applyInvoiceEntityIds will use targetInvoiceId=null for any (shouldn't be any) added entities.
    const draftInvoice = invoice.clone();
    draftInvoice.invoiceStatus = 'D';

    const {invoice: clearedInvoice, entities} = applyInvoiceEntityIds(draftInvoice, {
        taskIds: [],
        expenseIds: [],
        transactionIds: [],
        flatFeeIds: [],
        flatFeeMilestoneIds: [],
    });

    clearedInvoice.finalizedOn = null;
    clearedInvoice.finalValues = null;
    clearedInvoice.totalBilled = null;
    clearedInvoice.totalDue = null;
    clearedInvoice.amountPaid = null;

    return {invoice: clearedInvoice, entities};
}

/**
 * Add a single entity to a draft invoice.
 *
 * Convenience wrapper around `applyInvoiceEntityIds`: takes the current arrays,
 * appends this entity's id, and delegates. Caller doesn't touch invoiceId.
 */
export function associateEntityWithInvoice<T extends AssociableEntity>(
    entity: T,
    invoice: Invoice,
): { invoice: Invoice; entities: AssociableEntitySet } {
    if (invoice.invoiceStatus !== 'D') {
        throw new Error(`associateEntityWithInvoice: target invoice ${invoice.id} is not a draft (status=${invoice.invoiceStatus})`);
    }
    // For drafts, the invariant requires empty *Ids arrays. associating an entity
    // means it's a planning-only relationship; we can't actually populate the array
    // here without violating the invariant. The right time to populate arrays is at
    // finalize. So a single-entity "associate" on a draft is a no-op at the storage
    // level — the entity is in the matter's unbilled pool, the draft picks it up by
    // live computation. Throw to surface the misunderstanding loudly.
    throw new Error(`associateEntityWithInvoice: drafts cannot have entities directly associated. Entities are pulled in at finalize from the matter's unbilled pool.`);
}

/**
 * Remove a single entity from its current invoice.
 *
 * As with associate: removing an entity from a draft is meaningless because the
 * draft doesn't store entity associations. Removing from a finalized invoice
 * requires un-finalize first. Throws to enforce the workflow.
 */
export function dissociateEntityFromInvoice<T extends AssociableEntity>(
    entity: T,
    invoice: Invoice,
): { invoice: Invoice; entities: AssociableEntitySet } {
    if (invoice.invoiceStatus === 'F') {
        throw new Error(`dissociateEntityFromInvoice: invoice ${invoice.id} is finalized; un-finalize first via unfinalizeInvoice()`);
    }
    if (invoice.invoiceStatus === 'D') {
        throw new Error(`dissociateEntityFromInvoice: drafts don't store entity associations; this call is a no-op. Modify the entity's no-charge / hide flags to exclude it from the draft total instead.`);
    }
    throw new Error(`dissociateEntityFromInvoice: unsupported invoice status ${invoice.invoiceStatus}`);
}

/**
 * Map an entity instance to the matching `invoice.*Ids` field name.
 */
function _arrayFieldFor(entity: AssociableEntity): 'taskIds' | 'expenseIds' | 'transactionIds' | 'flatFeeIds' | 'flatFeeMilestoneIds' {
    if (entity instanceof Task) return 'taskIds';
    if (entity instanceof Expense) return 'expenseIds';
    if (entity instanceof Transaction) return 'transactionIds';
    if (entity instanceof FlatFeeMilestone) return 'flatFeeMilestoneIds';
    if (entity instanceof FlatFee) return 'flatFeeIds';
    throw new Error(`_arrayFieldFor: unsupported entity type ${(entity as object | null | undefined)?.constructor?.name}`);
}

// ============================================================================
// Dispatch helpers — commit a previously-computed result, or do apply+dispatch
// in one call. All dispatches happen in a single synchronous block so React 18+
// auto-batches the re-renders into one cycle.
// ============================================================================

/**
 * Commit the result of `applyInvoiceEntityIds` (or any of its wrappers like
 * `unfinalizeInvoice`) to the Redux store. All saves dispatch synchronously in
 * one tick — React 18 / react-redux 9 auto-batches the re-renders, and the
 * save-sagas for each entity type queue in parallel for the next server flush.
 *
 * Returns the committed invoice clone so callers can chain or await the saga's
 * eventual completion via other mechanisms.
 */
export function commitInvoiceEntityChanges(
    result: { invoice: Invoice; entities: AssociableEntitySet }
): Invoice {
    store.dispatch(saveInvoiceQueue(result.invoice));
    if (result.entities.tasks.length > 0) store.dispatch(saveManyTasksQueue(result.entities.tasks));
    if (result.entities.expenses.length > 0) store.dispatch(saveManyExpensesQueue(result.entities.expenses));
    if (result.entities.transactions.length > 0) store.dispatch(saveManyTransactionsQueue(result.entities.transactions));
    if (result.entities.flatFees.length > 0) store.dispatch(saveManyFlatFeesQueue(result.entities.flatFees));
    if (result.entities.flatFeeMilestones.length > 0) store.dispatch(saveManyFlatFeeMilestonesQueue(result.entities.flatFeeMilestones));
    return result.invoice;
}

/**
 * One-shot apply-and-commit: compute the association changes for the given
 * target `*Ids` set and dispatch everything in a single batched tick.
 *
 * This is the preferred entry point for callers — they pass the desired array
 * state, and the helper handles everything else (back-pointer propagation,
 * stamping, dispatching). Direct use of `applyInvoiceEntityIds` is reserved for
 * code paths that need to inspect or transform the result before dispatch.
 */
export function commitInvoiceEntityIds(invoice: Invoice, newIds: InvoiceEntityIds): Invoice {
    return commitInvoiceEntityChanges(applyInvoiceEntityIds(invoice, newIds));
}

/**
 * One-shot un-finalize: switch a finalized invoice back to draft and clear all
 * back-pointers, in a single batched tick. Wrapper over `unfinalizeInvoice` +
 * `commitInvoiceEntityChanges`.
 */
export function commitUnfinalizeInvoice(invoice: Invoice): Invoice {
    return commitInvoiceEntityChanges(unfinalizeInvoice(invoice));
}

/**
 * One-shot finalize without business-field setup. Most callers will NOT want this
 * directly — finalize requires totals, finalValues, billingIncrement, etc. to be
 * set after `finalizeInvoice` and before commit. Use `finalizeInvoice` + your
 * own field assignments + `commitInvoiceEntityChanges`. This helper exists for
 * symmetry and for callers that don't need the business-field tailoring.
 */
export function commitFinalizeInvoice(invoice: Invoice, entityIds: InvoiceEntityIds): Invoice {
    return commitInvoiceEntityChanges(finalizeInvoice(invoice, entityIds));
}

// ============================================================================
// Discount: special case — back-pointer-only association via entityType/entityId.
// ============================================================================

export function associateDiscountWithInvoice(discount: Discount, invoice: Invoice): { discount: Discount } {
    const clone = discount.clone();
    clone.entityType = 'invoice';
    clone.entityId = invoice.id;
    stamp(clone);
    return {discount: clone};
}

export function dissociateDiscount(discount: Discount): { discount: Discount } {
    const clone = discount.clone();
    clone.entityType = null as unknown as string;
    clone.entityId = null as unknown as string;
    stamp(clone);
    return {discount: clone};
}

// ============================================================================
// TODO: finalizeInvoice — needs buildFinalValues integration and full snapshot
// construction. Implement when refactoring InvoiceHelper.onToggleFinalize.
// ============================================================================
